/**
 * The published reads level 2 and level 3 are drawn from before the network-health read is
 * pinned — `docs/adr/9046`. One hook per question, each bounded by the run and the store or the
 * article (G16); none reads a run digest.
 *
 *   the run          `GET /inseason/runs` — its market and when it was recorded
 *   the rules        `GET /foundations/exception-rules` — each rule's comparator and threshold key
 *   the thresholds   `GET /registry/parameters/effective` — for the run's market ON the run's stock
 *                    date, as the network-health read resolves them (ADR 6055 §3)
 *   the positions    `GET /inseason/stock-positions?run_id&article_key|store_key`
 *   the lines        `GET /inseason/proposals` of the run, its store-to-store moves (below),
 *                    `GET /inseason/price-actions` of the tick's price run (DEC-109,
 *                    `./tick-runs.ts`), narrowed
 *   the moves        `GET /compacting/runs/{run_id}/moves?state=proposed&state=held` of the tick's
 *                    move run, by article or by origin and destination (bata-platform ADR 6055,
 *                    amended 2026-10-31) — or `GET /inseason/transfers` of a run recorded before the
 *                    move run (history), never both (`useStoreMoves`)
 *   the alerts       `GET /foundations/exceptions?open_only=true&store_key&article_key`
 *   the deferrals    `GET /inseason/runs/{run_id}/move-cap-deferrals?store_key|article_key` — what the
 *                    weekly move cap held back (bata-ui#302 C: «rinviato per tetto movimenti»)
 *   the unforecast   `GET /inseason/runs/{run_id}/report/replenishment/unforecast?store_key&article_key`
 *                    — the ranged sizes the run had no forecast for, and why (ADR 6182)
 */

import { useMemo } from 'react';

import { useApiCollection, useApiQuery } from '@/api';
import {
  effectiveParameter, foundationsListExceptions, inseasonListProposals, inseasonListRuns, listExceptionRules, listMoveCapDeferrals, listMoves,
  listPriceActions, listStockPositions, listTransfers, unforecastReport, type MoveListItem, type TransferSummary,
  type CycleRunSummary,
} from '@/api/generated';
import { readsTransfers } from '@/api/pending/network-health';

import { COVER_FLOOR_PARAMETER, COVER_RULE_ID, criterionOf, SIZE_RUN_RULE_ID, type Criteria, type StoreMoveLine } from './size-state';
import type { TickRuns } from './tick-runs';

/** The run's directory row — its market and its moment — or `undefined` while read / not listed. */
export function useRunRow(runId: string | null): { readonly row: CycleRunSummary | undefined; readonly directory: readonly CycleRunSummary[] | undefined } {
  const runs = useApiCollection(inseasonListRuns, { query: {} });
  return { row: runId === null ? undefined : runs.data?.find((run) => run.run_id === runId), directory: runs.data };
}

/** The stock date of a set of positions: their common `as_of` day, or the newest. */
export function stockDate(rows: readonly { readonly as_of: string }[]): string | null {
  let newest: string | null = null;
  for (const row of rows) {
    const day = row.as_of.slice(0, 10);
    if (newest === null || day > newest) newest = day;
  }
  return newest;
}

function numberOf(answer: { readonly source: string; readonly numericValue?: number | null } | undefined, failed: boolean): number | null | undefined {
  if (failed) return undefined;
  if (answer === undefined) return undefined;
  if (answer.source === 'unset') return null;
  return answer.numericValue ?? null;
}

/**
 * The two rules' tests and the cover rule's demand floor, for the run's market on its stock date.
 * Anything not yet read, or not readable, is an absence the states say (`notRead`), never a value
 * chosen here.
 */
export function useCriteria(market: string | null, asOf: string | null): Criteria {
  const rules = useApiQuery(listExceptionRules, {});
  const ruleOf = (ruleId: string) => (rules.isError ? undefined : rules.data === undefined ? undefined : rules.data.find((rule) => rule.ruleId === ruleId) ?? null);
  const cover = ruleOf(COVER_RULE_ID);
  const sizeGap = ruleOf(SIZE_RUN_RULE_ID);
  const ready = market !== null && asOf !== null;
  const effective = (key: string | undefined) => ({ query: { parameter_key: key ?? '', as_of: asOf ?? '', country: market } });
  const coverThreshold = useApiQuery(effectiveParameter, effective(cover?.thresholdParameterKey), { enabled: ready && cover !== undefined && cover !== null });
  const gapThreshold = useApiQuery(effectiveParameter, effective(sizeGap?.thresholdParameterKey), { enabled: ready && sizeGap !== undefined && sizeGap !== null });
  const floor = useApiQuery(effectiveParameter, effective(COVER_FLOOR_PARAMETER), { enabled: ready });
  return {
    cover: criterionOf(COVER_RULE_ID, cover, cover === null ? null : numberOf(coverThreshold.data, coverThreshold.isError)),
    sizeGap: criterionOf(SIZE_RUN_RULE_ID, sizeGap, sizeGap === null ? null : numberOf(gapThreshold.data, gapThreshold.isError)),
    demandFloor: numberOf(floor.data, floor.isError),
  };
}

/** One article's positions in the run, at every store — the scale, the projection, level 2. */
export function useArticlePositions(runId: string, articleKey: string) {
  return useApiCollection(listStockPositions, { query: { run_id: runId, article_key: articleKey } });
}

/** One store's positions in the run, every article — level 2 by store. */
export function useStorePositions(runId: string, storeKey: string) {
  return useApiCollection(listStockPositions, { query: { run_id: runId, store_key: storeKey } });
}

export function useProposals(runId: string, filter: { readonly storeKey?: string; readonly articleKey?: string }) {
  return useApiCollection(inseasonListProposals, {
    query: {
      run_id: runId,
      ...(filter.storeKey === undefined ? {} : { store_key: filter.storeKey }),
      ...(filter.articleKey === undefined ? {} : { article_key: filter.articleKey }),
    },
  });
}

/**
 * The run's transfers of one article, or at one store. Narrowed by the ARTICLE (both legs of every
 * line are then in hand) or by the store; the donor and recipient are told apart here.
 */
export function useTransfers(runId: string | null, filter: { readonly storeKey?: string; readonly articleKey?: string }) {
  return useApiCollection(listTransfers, {
    query: {
      run_id: runId ?? '',
      ...(filter.storeKey === undefined ? {} : { store_key: filter.storeKey }),
      ...(filter.articleKey === undefined ? {} : { article_key: filter.articleKey }),
    },
  }, { enabled: runId !== null });
}

/** The undecided alerts of the market on one article or one store. */
export function useOpenAlerts(market: string | null, filter: { readonly storeKey?: string; readonly articleKey?: string }) {
  return useApiCollection(foundationsListExceptions, {
    query: {
      open_only: true,
      ...(market === null ? {} : { country_key: market }),
      ...(filter.storeKey === undefined ? {} : { store_key: filter.storeKey }),
      ...(filter.articleKey === undefined ? {} : { article_key: filter.articleKey }),
    },
  }, { enabled: market !== null });
}

/** The run's price steps on one article — the «Articolo in ribasso» strip. */
export function usePriceSteps(runId: string | null, articleKey: string) {
  return useApiCollection(listPriceActions, { query: { run_id: runId ?? '', article_key: articleKey } }, { enabled: runId !== null });
}

/**
 * The run's move-cap deferrals at one store or of one article. A run older than the record answers
 * 409 (`move_cap_deferrals_not_recorded`): the read is then an error and the page marks nothing —
 * «not recorded» is not «nothing deferred», and neither is drawn as the other.
 */
export function useMoveCapDeferrals(runId: string | null, filter: { readonly storeKey?: string; readonly articleKey?: string }) {
  return useApiCollection(listMoveCapDeferrals, {
    path: { run_id: runId ?? '' },
    query: {
      ...(filter.storeKey === undefined ? {} : { store_key: filter.storeKey }),
      ...(filter.articleKey === undefined ? {} : { article_key: filter.articleKey }),
    },
  }, { enabled: runId !== null });
}

/**
 * The ranged sizes of one store × article that the run had no forecast for — `no_sales_in_window` or
 * `forecast_absent` (bata-platform ADR 6182). They get no target and no pull; the page only says why.
 * A run written before the platform recorded them answers an empty page, which draws nothing.
 */
export function useUnforecastSizes(runId: string | null, filter: { readonly storeKey?: string; readonly articleKey?: string }) {
  return useApiCollection(unforecastReport, {
    path: { run_id: runId ?? '' },
    query: {
      ...(filter.storeKey === undefined ? {} : { store_key: filter.storeKey }),
      ...(filter.articleKey === undefined ? {} : { article_key: filter.articleKey }),
    },
  }, { enabled: runId !== null });
}

/** No transfer lines — one stable value, so a failed read does not change a memo's inputs each render. */
export const NO_TRANSFERS: readonly TransferSummary[] = [];

/** The move-line states that count as a move (bata-platform ADR 6055, amended): kept and held. */
export const COUNTED_MOVE_STATES = ['proposed', 'held'] as const;

/**
 * One counted line of the move run as a store-to-store line (`StoreMoveLine`): its id, origin as the
 * donor, destination as the recipient, its newest decision. `null` for a row that is no line (a refused
 * candidate carries no size or store). Pure.
 */
export function moveLineOf(line: MoveListItem): StoreMoveLine | null {
  if (line.article_key === null || line.size_key === null || line.origin_store_key === null || line.destination_store_key === null || line.units === null) return null;
  if (!(COUNTED_MOVE_STATES as readonly string[]).includes(line.state)) return null;
  return {
    transfer_id: line.line_id,
    donor_store_key: line.origin_store_key,
    recipient_store_key: line.destination_store_key,
    article_key: line.article_key,
    size_key: line.size_key,
    units: line.units,
    disposition: line.disposition,
  };
}

/** No move lines — one stable value. */
export const NO_MOVES: readonly StoreMoveLine[] = [];

/**
 * The run's store-to-store lines of one article, or at one store (or both), from their one source
 * (bata-platform ADR 6055, amended 2026-10-31): the run's transfer rows when it wrote them (history),
 * the tick's move run's counted lines otherwise — both legs of an article's line by `article`, a
 * store's by `destination` and by `origin`. A run with neither has none. `data` is `undefined` while
 * a read is pending and while the tick's runs are; `isError` when a read failed.
 */
export function useStoreMoves(
  runId: string | null,
  runs: TickRuns | undefined,
  filter: { readonly storeKey?: string; readonly articleKey?: string },
): { readonly data: readonly StoreMoveLine[] | undefined; readonly isError: boolean; readonly refetch: () => void } {
  const history = runs !== undefined && readsTransfers(runs);
  const moveRun = runs?.moves ?? null;
  const transfers = useApiCollection(listTransfers, {
    query: {
      run_id: runId ?? '',
      ...(filter.storeKey === undefined ? {} : { store_key: filter.storeKey }),
      ...(filter.articleKey === undefined ? {} : { article_key: filter.articleKey }),
    },
  }, { enabled: runId !== null && history });
  const narrowed = (side: 'destination' | 'origin' | null) => ({
    path: { run_id: moveRun ?? '' },
    query: {
      state: [...COUNTED_MOVE_STATES],
      ...(filter.articleKey === undefined ? {} : { article: filter.articleKey }),
      ...(side === null || filter.storeKey === undefined ? {} : { [side]: filter.storeKey }),
    },
  });
  const byStore = filter.storeKey !== undefined;
  const inbound = useApiCollection(listMoves, narrowed(byStore ? 'destination' : null), { enabled: moveRun !== null });
  const outbound = useApiCollection(listMoves, narrowed('origin'), { enabled: moveRun !== null && byStore });
  const lines = useMemo(() => {
    if (moveRun === null) return undefined;
    const rows = [...(inbound.data ?? []), ...(byStore ? outbound.data ?? [] : [])];
    return rows.map(moveLineOf).filter((line): line is StoreMoveLine => line !== null);
  }, [moveRun, inbound.data, outbound.data, byStore]);
  const refetch = (): void => {
    if (history) void transfers.refetch();
    if (moveRun !== null) { void inbound.refetch(); if (byStore) void outbound.refetch(); }
  };
  if (runs === undefined) return { data: undefined, isError: false, refetch };
  if (history) return { data: transfers.isError ? NO_TRANSFERS : transfers.data, isError: transfers.isError, refetch };
  if (moveRun === null) return { data: NO_MOVES, isError: false, refetch };
  if (inbound.isError || (byStore && outbound.isError)) return { data: NO_MOVES, isError: true, refetch };
  if (inbound.data === undefined || (byStore && outbound.data === undefined)) return { data: undefined, isError: false, refetch };
  return { data: lines, isError: false, refetch };
}
